// Uniswap V3 tick/price math, ported from dotone-swap-v3's src/lib/v3.ts
// (ethers.BigNumber -> native bigint).

export const Q96 = 2n ** 96n;

export const MIN_TICK = -887272;
export const MAX_TICK = 887272;

export const TICK_SPACING: Record<number, number> = { 100: 1, 500: 10, 3000: 60, 10000: 200 };

export const MIN_SQRT_RATIO = 4295128739n;
export const MAX_SQRT_RATIO = 1461446703485210103287273052203988822378723970342n;

export function tickToPrice(tick: number, decimals0 = 18, decimals1 = 18): number {
  return Math.pow(1.0001, tick) * Math.pow(10, decimals0 - decimals1);
}

export function priceToTick(price: number, decimals0 = 18, decimals1 = 18): number {
  const adjustedPrice = price / Math.pow(10, decimals0 - decimals1);
  return Math.round(Math.log(adjustedPrice) / Math.log(1.0001));
}

export function nearestUsableTick(tick: number, tickSpacing: number): number {
  const rounded = Math.round(tick / tickSpacing) * tickSpacing;
  if (rounded < MIN_TICK) return MIN_TICK + tickSpacing;
  if (rounded > MAX_TICK) return MAX_TICK - tickSpacing;
  return rounded;
}

export function sqrtPriceToPrice(sqrtPriceX96: bigint, decimals0 = 18, decimals1 = 18): number {
  const sqrtPrice = Number(sqrtPriceX96);
  const q96 = Number(Q96);
  const ratio = sqrtPrice / q96;
  const price = ratio * ratio;
  return price * Math.pow(10, decimals0 - decimals1);
}

// Reverse of sqrtPriceToPrice, computed via a scaled integer to avoid JS
// float overflow when converting to bigint.
export function computeSqrtPriceX96(price: number): bigint {
  const PRECISION = 1e15;
  const sqrtP = Math.sqrt(price);
  const scaledSqrt = BigInt(Math.floor(sqrtP * PRECISION));
  return (Q96 * scaledSqrt) / BigInt(PRECISION);
}

export function feeToPercent(fee: number): string {
  return (fee / 10000).toFixed(fee < 1000 ? 2 : 1) + "%";
}

export interface CounterpartDepositParams {
  /** True when the user is typing into tokenA's box (as opposed to tokenB's). */
  changedIsTokenA: boolean;
  /** True when tokenA is the pool's sorted token0 (address0 < address1). */
  sorted: boolean;
  /** Price range bounds as entered in the UI (token1 per token0). */
  minPrice: string;
  maxPrice: string;
  /** Live pool price (token1 per token0), or 0 if the pool doesn't exist yet. */
  currentPrice: number;
  /** The raw value just typed into the changed field. */
  value: string;
  /** Decimals of the *other* (to-be-filled) token, for rounding the result. */
  otherDecimals: number;
}

// Ported from dotone-swap-v3's V3Liquidity.tsx calcCounterpartDeposit() — the
// same amount0/amount1 <-> liquidity math V3 positions use (real ratio for
// the chosen price range + current pool price) to auto-fill the other
// deposit box from the one the user is typing into.
//
// Returns "" to clear the other field (empty/invalid input), null to leave
// it untouched (no valid range yet, or an out-of-range single-sided
// position with no meaningful counterpart), or the computed amount string.
export function calcCounterpartDeposit({
  changedIsTokenA,
  sorted,
  minPrice,
  maxPrice,
  currentPrice,
  value: rawValue,
  otherDecimals,
}: CounterpartDepositParams): string | null {
  const value = parseFloat(rawValue);
  if (!rawValue || !Number.isFinite(value) || value <= 0) return "";

  const mp = parseFloat(minPrice);
  const xp = parseFloat(maxPrice);
  if (!mp || !xp || xp <= mp) return null;

  // Same sorted-convention conversion the mint tick math uses.
  const priceLower = sorted ? mp : 1 / xp;
  const priceUpper = sorted ? xp : 1 / mp;
  if (!(priceLower > 0) || !(priceUpper > priceLower)) return null;

  const sqrtLower = Math.sqrt(priceLower);
  const sqrtUpper = Math.sqrt(priceUpper);
  // If there's no live pool yet, assume it'll initialize at the midpoint of
  // the chosen range purely as a starting ratio estimate.
  const sqrtCurrent = currentPrice > 0 ? Math.sqrt(currentPrice) : Math.sqrt(priceLower * priceUpper);

  const changedIsSortedToken0 = sorted ? changedIsTokenA : !changedIsTokenA;

  let otherAmount: number;
  if (sqrtCurrent <= sqrtLower) {
    // Position sits entirely below the current price -> 100% token0 (sorted).
    otherAmount = changedIsSortedToken0 ? 0 : NaN;
  } else if (sqrtCurrent >= sqrtUpper) {
    // Position sits entirely above the current price -> 100% token1 (sorted).
    otherAmount = changedIsSortedToken0 ? NaN : 0;
  } else if (changedIsSortedToken0) {
    const liquidity = (value * (sqrtCurrent * sqrtUpper)) / (sqrtUpper - sqrtCurrent);
    otherAmount = liquidity * (sqrtCurrent - sqrtLower);
  } else {
    const liquidity = value / (sqrtCurrent - sqrtLower);
    otherAmount = (liquidity * (sqrtUpper - sqrtCurrent)) / (sqrtCurrent * sqrtUpper);
  }

  if (!Number.isFinite(otherAmount)) return null;
  return Math.max(0, otherAmount).toFixed(Math.min(6, otherDecimals));
}
